<template>
  <header id="nav">
    <div class="nav__left">
      <router-link
        class="nav__logo-wrap"
        to="/"
        @click.native="closeMobileMenu"
      >
        <img class="nav__logo" alt="Meqyas Logo" src="/img/meqyas-logo.svg" />
        <img
          src="img/CST-Header.svg"
          class="nav__logo nav__logo--desktop"
          alt="CST Logo"
        />
        <img
          class="nav__logo nav__logo--mobile"
          alt="CST Mobile Logo"
          src="/img/CST-Header-Mobile.svg"
        />
      </router-link>
    </div>

    <nav ref="nav">
      <LanguageToggle />
      <ul
        :class="{ 'nav__links--mobile-open': isMobileNavOpen }"
        class="nav__links"
        @click="closeMobileMenu"
      >
        <li>
          <router-link
            :class="{ 'nav__link-active': isTabActive('/test-kit') }"
            to="/test-kit"
            class="nav__link"
            exact
          >
            {{ $t('views.MeqyasTestkit.title') }}
          </router-link>
        </li>
        <li>
          <a href="/faqs" class="nav__link">
            {{ $t('views.FAQs.title') }}
          </a>
        </li>
        <li>
          <router-link
            :class="{ 'nav__link-active': isTabActive('/reports') }"
            to="/reports"
            class="nav__link"
            exact
          >
            {{ $t('views.Reports.title') }}
          </router-link>
        </li>
        <li>
          <router-link
            :class="{ 'nav__link-active': isTabActive('/contact') }"
            to="/contact"
            class="nav__link"
            exact
          >
            {{ $t('views.Contact.title') }}
          </router-link>
        </li>
        <li v-if="!isMobileNavOpen">
          <router-link
            :class="{ 'nav__link-active': isTabActive('/signup') }"
            class="nav__link nav__link--signup"
            to="/signup"
          >
            {{ $t('views.Signup.title') }}
            <Icon icon="platform" />
          </router-link>
        </li>
        <li v-if="isMobileNavOpen">
          <div class="nav__link-group">
            <router-link class="nav__link" to="/signup">{{
              $t('views.Signup.title')
            }}</router-link>
          </div>
        </li>
      </ul>
    </nav>

    <button
      class="nav__menu-toggle btn btn-link"
      @click="isMobileNavOpen = !isMobileNavOpen"
    >
      <Icon v-if="!isMobileNavOpen" icon="hamburger" />
      <span v-if="isMobileNavOpen" class="icon" aria-hidden="true">
        &#10005;
      </span>
    </button>
  </header>
</template>

<script>
import Icon from '@/components/Icon';
import LanguageToggle from '@/components/LanguageToggle';

export default {
  components: {
    LanguageToggle,
    Icon
  },
  props: {
    isSidebarAvailable: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isMobileNavOpen: false
    };
  },
  computed: {
    isEng() {
      return this.$skI18n.locale === 'en-GB';
    }
  },
  methods: {
    closeMobileMenu(e) {
      if (e.target.tagName === 'SPAN') {
        return;
      }
      this.isMobileNavOpen = false;
    },
    isTabActive(tab) {
      return this.$route.path.includes(tab);
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../scss/base/colors';
@import '../scss/helpers/mixins';
@import '../scss/base/variables';

#nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: $navbar-height;

  background: $white;
  border-top: 5px solid $primary;
  box-shadow: 0 1px 2px rgba(black, 0.15);

  nav {
    display: flex;
  }
}

.nav__left {
  display: flex;
  align-items: center;

  [dir='rtl'] & {
    margin-right: 0;
  }
}

.nav__logo {
  height: 45px;

  &--desktop {
    margin-left: 1em;

    [dir='rtl'] & {
      margin-left: 0;
      margin-right: 1em;
    }
    @include breakpoint-max(XS) {
      display: none;
    }
  }

  &--mobile {
    margin-left: 1em;

    [dir='rtl'] & {
      margin-left: 0;
      margin-right: 1em;
    }
    @include breakpoint-max(XS) {
      display: inline;
    }
    @include breakpoint(XS) {
      display: none;
    }
  }
}

.nav__logo-wrap {
  height: 55px;
  display: flex;
  align-items: center;
  margin: 0 10px;
}

.nav__links {
  z-index: 5;
  position: fixed;
  top: 60px;
  left: 0;
  right: 0;
  bottom: 0;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  visibility: hidden;

  white-space: nowrap;

  list-style: none;

  transition: transform 0.2s ease-in-out;
  transform: translateY(-100%);
  pointer-events: none;

  @include breakpoint(M) {
    position: static;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    padding-top: 0;
    visibility: visible;

    opacity: 1;
    background: none;
    color: inherit;

    transform: none;
    pointer-events: all;
  }

  .nav__link--signup {
    color: $titles;
    [dir='rtl'] & {
      padding-right: 5px;
    }
  }
}

.nav__links--mobile-open {
  visibility: visible;
  pointer-events: all;
  transform: none;

  .nav__link-group {
    width: 100vw;
    display: flex;

    .nav__link {
      display: flex;
      align-items: center;
      justify-content: center;

      color: $titles;
      &:first-child {
        border-right: 0;
      }
    }
  }

  .nav__link {
    width: 100vw;
  }
}

.nav__link {
  background: $white;
  border-bottom: 1px solid $grey-3;
  @include breakpoint(M) {
    border-bottom: 3px solid transparent;
  }
  display: flex;
  align-items: center;
  height: 55px;
  padding: 0 15px;
  text-decoration: none;

  color: rgba($primary, 0.75);

  &.nav__link-active {
    position: relative;
    font-weight: 600;
    text-decoration: none;
    background: $beige;
    color: $primary;
    @include breakpoint(M) {
      background: none;
      border-bottom: 3px solid $primary;
    }
  }
  &.nav__link-active:before {
    position: absolute;
    left: -1px;
    content: '';
    width: 5px;
    height: 56px;
    background: $primary;
    @include breakpoint(M) {
      display: none;
    }
  }

  .icon {
    margin: 0 0 0 15px;

    [dir='rtl'] & {
      margin: 0 15px 0 0;
    }
  }
}

.nav__sidebar-toggle {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 55px;
  padding: 0 15px;

  color: inherit;
  text-decoration: none;

  @include breakpoint(M) {
    display: none;
  }
}

.nav__menu-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 55px;
  height: 55px;
  margin-left: 15px;
  padding-bottom: 1px;

  color: $primary;
  text-decoration: none;
  border-left: 1px $grey-3 solid;

  [dir='rtl'] & {
    margin-left: 0;
    margin-right: 15px;

    border-left: none;
    border-right: 1px $grey-3 solid;
  }

  @include breakpoint(M) {
    display: none;
  }
  .icon {
    font-size: 24px;
  }
}
</style>
